import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Gift, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { track } from "../lib/analytics";

/**
 * RedeemCard — Settings card where a user types in a gift code from
 * a personal DM and instantly gets months of Pro added.
 *
 * Success state celebrates with the founding-member language —
 * builds belonging instead of just confirming a transaction.
 */
const RedeemCard = () => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    const trimmed = code.trim();
    if (!trimmed) return;
    setBusy(true);
    track("gift_code_redeem_attempt");
    try {
      const { data } = await api.post("/redeem", { code: trimmed });
      setSuccess(data);
      setCode("");
      track("gift_code_redeem_success", {
        months_granted: data.months_granted,
      });
      toast.success(data.message || "You're in. Welcome to Pro 💜");
    } catch (err) {
      track("gift_code_redeem_failed", {
        status: err?.response?.status,
      });
      toast.error(
        err?.response?.data?.detail ||
          "Something went wrong redeeming that code.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-white/80 backdrop-blur-xl border border-[#EADDD7] rounded-3xl p-6 sm:p-7 soft-shadow mb-5"
      data-testid="redeem-card"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-full bg-[#F2E8E9] flex items-center justify-center shrink-0">
          <Gift className="h-5 w-5 text-[#9B6A6C]" />
        </div>
        <div className="flex-1 min-w-0">
          <h3
            className="text-xl font-medium text-[#2C2123]"
            data-testid="redeem-heading"
          >
            Have a gift code?
          </h3>
          <p className="text-sm text-[#5C4B51] leading-relaxed mt-1">
            If someone shared a code with you, paste it here to unlock
            your months of Pro.
          </p>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {success ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="rounded-2xl bg-gradient-to-br from-[#F5DCD7]/60 to-[#FAF5F1] border border-[#EADDD7] p-5"
            data-testid="redeem-success"
          >
            <div className="flex items-center gap-2 text-[#5D4050] text-sm">
              <Sparkles className="h-4 w-4 text-[#9B6A6C]" />
              <span className="font-medium">
                {success.months_granted} month
                {success.months_granted === 1 ? "" : "s"} of Pro added
              </span>
            </div>
            <p className="text-sm text-[#5C4B51] mt-2 leading-relaxed">
              {success.message}
            </p>
            <button
              type="button"
              onClick={() => setSuccess(null)}
              className="text-xs text-[#9B6A6C] hover:text-[#7d5455] underline underline-offset-2 mt-3"
              data-testid="redeem-success-dismiss"
            >
              Redeem another
            </button>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onSubmit={submit}
            className="flex flex-col sm:flex-row gap-2"
          >
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="FOUNDER-XXXX"
              maxLength={40}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              className="flex-1 px-4 py-2.5 rounded-xl border border-[#EADDD7] bg-white focus:outline-none focus:border-[#9B6A6C] text-sm tracking-wider font-mono"
              data-testid="redeem-input"
            />
            <button
              type="submit"
              disabled={busy || !code.trim()}
              className="inline-flex items-center justify-center gap-2 bg-[#9B6A6C] hover:bg-[#7d5455] text-white rounded-xl px-5 py-2.5 text-sm font-medium transition-colors disabled:opacity-50"
              data-testid="redeem-submit"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Redeem"
              )}
            </button>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RedeemCard;
